import { normalizeNovaRequest, mentionsOtherReportType } from './nova-request-intent';

export interface ReportCandidate {
  id: string;
  name: string;
  type: string;
  userId: string;
  shared: boolean;
  createdAt: string | Date;
}

export interface CompletedWebsiteReport {
  scanProjectId: string;
  scanType: string;
  status: string;
  canceled: boolean;
  metadata?: { websiteUrl?: string };
}

export class ReportDiscoveryError extends Error {}

/** Explicit standalone-chat requests only; ordinary questions and named URLs stay unchanged. */
export function requestsLatestWebsiteReport(text: string, websiteContext = false): boolean {
  const s = normalizeNovaRequest(text);
  return /\b(latest|last|most recent|newest|ultima|ultimo|mais recente)\b/.test(s)
    && !mentionsOtherReportType(s) && (/\b(website|site)\b/.test(s) || websiteContext)
    && /\b(report|reports|analysis|analyses|scan|scans|relatorio|relatorios|analise|analises|one)\b/.test(s)
    && !/\b(don't|dont|do not|nao)\b/.test(s)
    && !/\b(last|latest|ultimos?|ultimas?)\s+(\d+|two|three|four|five|week|month|year|semana|mes|ano)\b/.test(s)
    && !/\b(report|analysis|scan)\s+(for|from)\s+(?!my\b|our\b|the website\b|the site\b)/.test(s)
    && !/https?:\/\/|\b[a-z0-9-]+\.(com|org|net|io|rocks)\b/.test(s)
    && !/\b(delete|remove|rerun|rescan|create|start|cancel|compare|exclu\w*|apag\w*|remov\w*|criar|iniciar|cancelar|compar\w*)\b/.test(s);
}

/** Latest = dashboard scan date (createdAt), among completed, accessible website scans. */
export async function discoverLatestWebsiteReport(options: {
  userId: string;
  page: (index: number) => Promise<{ projects: ReportCandidate[]; count: number }>;
  report: (id: string) => Promise<CompletedWebsiteReport>;
  maxPages?: number;
  maxReports?: number;
}): Promise<{ project: ReportCandidate; report: CompletedWebsiteReport }> {
  let previousDate = Infinity;
  let checked = 0;
  const seen = new Set<string>();
  let found: { project: ReportCandidate; report: CompletedWebsiteReport } | undefined;
  for (let index = 0; index < (options.maxPages ?? 4); index++) {
    const page = await options.page(index);
    if (!Array.isArray(page.projects) || !Number.isFinite(page.count) || page.count < 0) {
      throw new ReportDiscoveryError('The report list could not be verified. Please try again.');
    }
    for (const project of page.projects) {
      const date = new Date(project.createdAt).getTime();
      if (!Number.isFinite(date) || date > previousDate || seen.has(project.id)) {
        throw new ReportDiscoveryError('The report list changed while we were checking it. Please try again.');
      }
      previousDate = date;
      seen.add(project.id);
      if (found && date < new Date(found.project.createdAt).getTime()) return found;
      // Defense in depth: the API already scopes this list to the signed-in user and organization.
      if (project.type !== 'website' || (project.userId !== options.userId && project.shared !== true)) continue;
      if (++checked > (options.maxReports ?? 20)) {
        throw new ReportDiscoveryError('Too many recent scans to identify a completed website report in one check. Please narrow the request.');
      }
      let report: CompletedWebsiteReport;
      try { report = await options.report(project.id); }
      catch { throw new ReportDiscoveryError(`Could not read the status of "${project.name}". We cannot safely choose an older report as the latest. Please try again.`); }
      if (report.scanProjectId !== project.id || report.scanType !== 'website') {
        throw new ReportDiscoveryError('The website report did not match the selected scan. Please try again.');
      }
      if (!['PENDING','SCRAPING','SCANNING','ERROR','SCRAPED','AI_PROCESSED','AI_PROCESSING',
        'SCANNED','SKIPPED','DUPLICATING','EXPORTING-REPORT','CANCELED'].includes(report.status)
        || typeof report.canceled !== 'boolean') {
        throw new ReportDiscoveryError('The scan status could not be verified. Please try again.');
      }
      if (report.status !== 'SCANNED' || report.canceled) continue;
      if (found) {
        throw new ReportDiscoveryError(`Two completed website reports have the same scan date: "${found.project.name}" and "${project.name}". Which one should we review?`);
      }
      found = { project, report };
    }
    if (seen.size >= page.count) {
      if (found) return found;
      throw new ReportDiscoveryError('No completed website report is available in this organization among our own and shared reports.');
    }
    if (!page.projects.length) throw new ReportDiscoveryError('The report list is incomplete. Please try again.');
  }
  throw new ReportDiscoveryError('The report search reached its limit before finding the latest completed website scan. Please narrow the request.');
}
